import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * The try-it-here lesson on the marketing page.
 *
 * This REPLICATES the opening of the real Study Lab lesson (origins2, "The Iron
 * Curtain") rather than inventing teaching content. Steps 1–6 below are that
 * lesson's own words and its own illustration: the two accounts of the
 * neighbour dispute, the question it puts to you, the "Why" it gives back, the
 * turn that names the skill, the evidence, and the map. Do not rewrite them
 * here — if the lesson changes, copy the change across.
 *
 * Only the closing step is ours, and only because it has to be. The real lesson
 * carries on to Source A; on a homepage the next step is "start free".
 *
 * It ends on the map deliberately. Everything before it is an argument about
 * whose account to believe, and the map is the moment that argument turns into
 * ground you can see — which makes it the right last thing to leave someone
 * with before asking them to sign up.
 */

interface ChoiceOption {
  key: string;
  text: string;
  correct?: boolean;
}

type Beat = { when: string; text: string };

interface KeyTerm {
  term: string;
  def: string;
}

type Step =
  | { kind: 'beats'; title: string; strap: string; beats: Beat[]; close?: string[] }
  | { kind: 'choice'; kicker: string; prompt: string; options: ChoiceOption[]; why: string }
  | {
      kind: 'prose';
      kicker: string;
      title: string;
      body: string[];
      terms?: KeyTerm[];
      /** The lesson's own illustration, shown after the body. */
      figure?: 'iron-curtain';
    }
  | { kind: 'close'; title: string; body: string };

const STEPS: Step[] = [
  {
    kind: 'beats',
    title: 'Side one',
    strap: 'Two families · one wall · three years',
    beats: [
      { when: 'Year one', text: 'They play music late. You knock on the wall. They stop greeting you for a month.' },
      { when: 'Year two', text: 'Your mother passes their gate and looks straight ahead. They started it, so she owes them nothing.' },
      { when: 'Year three', text: 'You plant a tree. They complain that it will take the light from their kitchen.' },
      { when: 'Last week', text: 'They cut the branch that hung over their side. Nobody asked you first.' },
      { when: 'Today', text: 'You could say exactly who started this.' },
    ],
  },
  {
    kind: 'beats',
    title: 'Side two',
    strap: 'The same wall · the same three years',
    beats: [
      { when: 'Year one', text: 'Someone bangs on the wall at eight in the evening. Your father decides they are not worth speaking to.' },
      { when: 'Year two', text: 'The woman next door walks past your gate and looks straight ahead. She started it.' },
      { when: 'Year three', text: 'They plant a tree hard against the line, where your kitchen gets its light.' },
      { when: 'Last week', text: 'Your father cuts the branch on your own side of the fence. He asks nobody, because why would he.' },
      { when: 'Today', text: 'You could say exactly who started this.' },
    ],
    close: [
      'Same wall. Same three years. Nobody lied.',
      'You settle arguments like this every week. Now hand the same argument to two governments who can destroy each other.',
    ],
  },
  {
    kind: 'choice',
    kicker: 'Instinct',
    prompt: 'Which family started it?',
    options: [
      { key: 'A', text: 'The family who first complained about the music late at night' },
      { key: 'B', text: 'Neither, because nobody watched the whole three years from outside' },
      { key: 'C', text: 'The family who planted the tree along the boundary line' },
      { key: 'D', text: 'Both accounts are true, and neither one settles who started it', correct: true },
    ],
    // One explanation, shown whatever you pick — the lesson's own design. It
    // opens by removing the score, which is the opposite of a quiz and the
    // reason this is the piece worth showing.
    why:
      'There is no score on this one. Every event in both lists happened, and neither side left anything out. What separates the two accounts is not evidence. It is what each family had already decided about the other. You are about to watch two governments do this to each other, thirteen days apart, and neither of them is lying.',
  },
  {
    kind: 'prose',
    kicker: 'The turn',
    title: 'The word for what you just did',
    body: [
      'You did not settle who started it. You worked out that each account was built out of where the teller was standing.',
      'That is perspective: what a source argues for depends on what it is afraid of.',
      'Paper 1 asks this directly. How does Source A differ from Source B? The command word wants both positions named and set against each other. Give only one side and you have written half an answer.',
    ],
    terms: [
      { term: 'Perspective', def: 'Where a source stands on an issue, and how that position shapes what it says.' },
      {
        term: 'Compare and contrast',
        def: 'The hardest of the three source skills. Set two sources against each other and say how they differ or how they agree.',
      },
    ],
  },
  {
    kind: 'prose',
    kicker: 'The evidence',
    title: 'Who was actually standing where',
    body: [
      'The Red Army had not gone home. It had driven the Germans back across Poland, Romania, Bulgaria, Hungary and into eastern Germany, and it stayed.',
      'Whoever holds the ground decides who runs the country. Communist ministers took the key posts, opposition parties were merged or banned, and elections either did not happen or produced one answer.',
      'By 1948 eight states in Eastern Europe had governments loyal to Moscow. They are called satellite states: formally independent, actually steered from outside.',
    ],
    terms: [
      { term: 'Sphere of influence', def: 'An area a great power controls or steers without formally ruling it.' },
      {
        term: 'Satellite state',
        def: "A country that is independent on paper but whose government follows another power's instructions.",
      },
      {
        term: 'Red Army',
        def: 'The Soviet army. Its position in Eastern Europe in 1945 is the single biggest fact of this lesson.',
      },
    ],
  },
  {
    kind: 'prose',
    kicker: 'The evidence',
    title: 'Czechoslovakia, February 1948',
    body: [
      'Czechoslovakia was the one that mattered most to Western opinion. It was the only Eastern European country with a real democratic tradition, and parties had really competed in its 1946 election.',
      'In February 1948 the communist members of the coalition government took control of the police, forced the non-communist ministers out and took power outright. The foreign minister, Jan Masaryk, was found dead below a window two weeks later.',
      'For anyone in the West still arguing that Soviet policy was defensive, February 1948 ended the argument. It is also why the Marshall Plan passed Congress the following month, which is Lesson 3.',
    ],
    figure: 'iron-curtain',
  },
  {
    // Ours. The real lesson goes on to Source A; this page cannot.
    kind: 'close',
    title: 'Those are the first six steps of a Study Lab.',
    body:
      'Each Study Lab consists of two knowledge quizzes and a series of five lessons, covering all the syllabus content while developing your source analysis and essay-writing skills.',
  },
];

/**
 * The closing panel is ours, not one of the lesson's steps, so it is outside the
 * count — the counter has to agree with the number the close puts in words.
 */
const CONTENT_STEPS = STEPS.filter((s) => s.kind !== 'close').length;

export const ReviseTryLesson: React.FC<{ id?: string; tag?: string }> = ({ id, tag }) => {
  /* The demo opens on a COVER, not on step 1. Rendered mid-page, step 1 read
     as a screenshot — the one thing this block must not be mistaken for — so
     the first thing it asks for is a click, and the click is the proof it is
     alive (Dylan, 1 Sep). */
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [chosen, setChosen] = useState<Record<number, string>>({});
  const stageRef = useRef<HTMLDivElement | null>(null);

  const step = STEPS[index];
  const isLast = index === STEPS.length - 1;
  const answer = chosen[index];

  // A choice will not let you past until you commit. That gate is the point of
  // the step — reading four options is not the same as picking one.
  const blocked = step.kind === 'choice' && !answer;

  const restart = useCallback(() => {
    // Clearing `chosen` is the point. Without it a second run opens the choice
    // step already answered, with the "Why" expanded and the gate skipped —
    // which is the one step that has to be worked to mean anything.
    setChosen({});
    setIndex(0);
    window.requestAnimationFrame(() => stageRef.current?.focus());
  }, []);

  const go = useCallback(
    (next: number) => {
      setIndex(next);
      // Move focus to the stage so the change is announced and keyboard users
      // are not left at the bottom of the page after the panel swaps.
      window.requestAnimationFrame(() => stageRef.current?.focus());
    },
    [],
  );

  const begin = useCallback(() => {
    setStarted(true);
    window.requestAnimationFrame(() => stageRef.current?.focus());
  }, []);

  const progress = useMemo(
    () => (!started ? 0 : isLast ? 100 : ((index + 1) / CONTENT_STEPS) * 100),
    [started, index, isLast],
  );

  const stepLabel = !started
    ? `${CONTENT_STEPS} short steps`
    : isLast
      ? `Step ${CONTENT_STEPS} of ${CONTENT_STEPS} · done`
      : `Step ${index + 1} of ${CONTENT_STEPS}`;

  return (
    <div className="rv-lesson" id={id} data-accent="study">
      <div className="rv-lesson-bar">
        {tag && <span className="rv-lesson-tag">{tag}</span>}
        <span className="rv-lesson-count">{stepLabel}</span>
        <span className="rv-lesson-track" aria-hidden="true">
          <span className="rv-lesson-fill" style={{ width: `${progress}%` }} />
        </span>
      </div>

      <div
        className="rv-lesson-stage"
        ref={stageRef}
        tabIndex={-1}
        role="group"
        aria-label={stepLabel}
      >
        {!started && (
          <div className="rv-lesson-cover">
            <p className="rv-lesson-kicker">This is not a screenshot</p>
            <h4 className="rv-lesson-title">The real lesson, running right here.</h4>
            <p className="rv-lesson-body">
              Six short steps from the opening of a Study Lab, two or three minutes. You read, you
              choose, it answers back — exactly what a student gets.
            </p>
            <button type="button" className="rv-lesson-next rv-lesson-begin" onClick={begin}>
              Start the lesson &rarr;
            </button>
          </div>
        )}

        {started && step.kind === 'beats' && (
          <>
            <h4 className="rv-lesson-title">{step.title}</h4>
            <p className="rv-lesson-strap">{step.strap}</p>
            <ol className="rv-lesson-beats">
              {step.beats.map((b) => (
                <li key={b.when}>
                  <span className="rv-lesson-when">{b.when}</span>
                  <span className="rv-lesson-beat">{b.text}</span>
                </li>
              ))}
            </ol>
            {step.close?.map((line, i) => (
              <p key={line.slice(0, 24)} className={i === 0 ? 'rv-lesson-close' : 'rv-lesson-pull'}>
                {line}
              </p>
            ))}
          </>
        )}

        {started && step.kind === 'choice' && (
          <>
            <p className="rv-lesson-kicker">{step.kicker}</p>
            <p className="rv-lesson-prompt">{step.prompt}</p>
            <div className="rv-lesson-options">
              {step.options.map((o) => {
                const picked = answer === o.key;
                // Marks appear only once an answer is in. Before that the four
                // read as equals, which is the point of asking.
                const mark = !answer ? null : o.correct ? '✓' : picked ? '✗' : null;
                return (
                  <button
                    key={o.key}
                    type="button"
                    className="rv-lesson-option"
                    aria-pressed={picked}
                    data-picked={picked ? 'true' : 'false'}
                    data-mark={mark ? (o.correct ? 'yes' : 'no') : undefined}
                    onClick={() => setChosen((prev) => ({ ...prev, [index]: o.key }))}
                  >
                    <span className="rv-lesson-key">{o.key}</span>
                    <span>{o.text}</span>
                    {mark && (
                      <span className="rv-lesson-mark" aria-hidden="true">
                        {mark}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {answer && (
              <div className="rv-lesson-shows" role="status">
                <p className="rv-lesson-kicker">Why</p>
                <p className="rv-lesson-body">{step.why}</p>
              </div>
            )}
          </>
        )}

        {started && step.kind === 'prose' && (
          <>
            {/* With a figure this becomes two columns on desktop: the map is a
                block beside the argument rather than a full-width slab under
                it. The copy keeps a readable measure and the illustration stops
                dominating the step it is supporting. */}
            <div className="rv-lesson-prose" data-with-figure={step.figure ? 'true' : undefined}>
              <div className="rv-lesson-prose-copy">
                <p className="rv-lesson-kicker">{step.kicker}</p>
                <h4 className="rv-lesson-subtitle">{step.title}</h4>
                {step.body.map((para) => (
                  <p className="rv-lesson-body" key={para.slice(0, 30)}>
                    {para}
                  </p>
                ))}
              </div>
            {step.figure === 'iron-curtain' && (
              <figure className="rv-lesson-figure">
                <img
                  src="/marketing/revise/iron-curtain-1949.svg"
                  alt="Map of Europe divided by the iron curtain in 1949"
                  width={620}
                  height={470}
                  loading="lazy"
                />
                <ul className="rv-lesson-legend">
                  <li data-swatch="east">Soviet Union and the states loyal to Moscow</li>
                  <li data-swatch="west">Western states, most of them in NATO from 1949</li>
                  <li data-swatch="neutral">Neutral: Sweden, Finland, Switzerland, Ireland, Austria</li>
                  <li data-swatch="split">Communist but outside Soviet control: Yugoslavia and Albania</li>
                </ul>
                <div className="rv-lesson-inset">
                  <p className="rv-lesson-kicker">Germany, divided</p>
                  <img
                    src="/marketing/revise/germany-divided-1949.svg"
                    alt="Germany split into western zones and a Soviet zone, with Berlin inside the Soviet zone"
                    width={330}
                    height={108}
                    loading="lazy"
                  />
                  <p className="rv-lesson-caption">
                    Berlin was split into four sectors, the same as the country. It sat about 160
                    kilometres inside the Soviet zone.
                  </p>
                </div>
                <figcaption className="rv-lesson-caption">
                  Europe by 1949. The heavy line is the border Churchill had called an iron curtain
                  three years earlier. Two things are worth noticing. Yugoslavia and Albania are
                  communist and outside Soviet control, so the division was never simply communist
                  against capitalist. And look at Berlin, because that is where this unit ends up.{' '}
                  <span>Original illustration for Kula Revise. Boundaries from Natural Earth, public domain.</span>
                </figcaption>
              </figure>
            )}
            </div>
            {step.terms && (
              <div className="rv-lesson-terms">
                <p className="rv-lesson-kicker">Key terms · open any of these</p>
                {step.terms.map((t) => (
                  <details key={t.term}>
                    <summary>{t.term}</summary>
                    <p>{t.def}</p>
                  </details>
                ))}
              </div>
            )}
          </>
        )}

        {started && step.kind === 'close' && (
          <>
            <h4 className="rv-lesson-title">{step.title}</h4>
            <p className="rv-lesson-body">{step.body}</p>
            <div className="rv-lesson-end">
              <Link className="rv-btn rv-btn-primary" to="/signup">
                Start free
              </Link>
              <button type="button" className="rv-lesson-restart" onClick={restart}>
                Take it again
              </button>
            </div>
          </>
        )}
      </div>

      {started && !isLast && (
        <div className="rv-lesson-nav">
          <button
            type="button"
            className="rv-lesson-back"
            onClick={() => go(index - 1)}
            disabled={index === 0}
          >
            &larr; Back
          </button>
          <button
            type="button"
            className="rv-lesson-next"
            onClick={() => go(index + 1)}
            disabled={blocked}
            /* Explains itself rather than just going dead: a disabled control
               with no reason is the most common way these gates annoy people. */
            title={blocked ? 'Choose an answer to continue' : undefined}
          >
            {blocked ? 'Choose an answer' : 'Continue →'}
          </button>
        </div>
      )}
    </div>
  );
};
